import React from "react";
import N8Emblem from "@/components/N8Emblem";

/**
 * PageHero — the single, minimal, cinematic banner used at the top of
 * EVERY N8ONE surface (primary pages and every sub-section). One shared
 * component so every hero is exactly the same vertical size.
 *
 * Deliberately quiet: the photograph sits behind a strong copper/ivory
 * wash so it feels like a mood, not a picture. Height is fixed compact.
 *
 * EMBLEM SYSTEM (global master rules, 2026-02):
 *   Every hero can display an N8ONE emblem in the top-right so the
 *   brand presence is consistent across the app without obscuring the
 *   human-centered imagery behind it. The `emblem` prop is the single
 *   switch:
 *     - "full"         → full static orb; MAJOR N8ONE experience
 *                        (Home, Becoming, main Soul Compass, main Soul
 *                        Work, Witness, My Journey, Capture, main
 *                        Rituals, main Journal, main Collective,
 *                        Circle, Wins).
 *     - "simplified"   → compact static mark; RECURRING / SECONDARY
 *                        brand presence (individual practice pages,
 *                        detail pages, secondary surfaces).
 *     - "guide"        → N8 Guide at rest (breathing).
 *     - "guide-active" → N8 Guide actively generating (brighter breath).
 *     - "none" / unset → no emblem (default; opt-in).
 *
 * Props:
 *   eyebrow    — small uppercase kicker line (e.g. "Today", "Your Journey")
 *   title      — the page/section title
 *   supporting — optional italic supporting line in copper
 *   body       — optional short descriptive paragraph
 *   image      — Unsplash-style URL for the background photograph
 *   align      — "left" (default) or "center"
 *   emblem     — one of "full" | "simplified" | "guide" | "guide-active" | "none"
 *   testid     — data-testid root
 */
export default function PageHero({
  eyebrow,
  title,
  supporting,
  body,
  image,
  align = "left",
  emblem = "none",
  testid = "page-hero",
  children,
}) {
  return (
    <section
      className="relative overflow-hidden rounded-[22px]"
      data-testid={testid}
      style={{ minHeight: "200px" }}
    >
      {/* Photograph layer — kept quiet by the wash below */}
      {image && (
        <div
          aria-hidden
          className="absolute inset-0 bg-center bg-cover"
          style={{ backgroundImage: `url("${image}")` }}
        />
      )}
      {/* Copper/ivory wash — a little heavier so the image never overpowers */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            align === "center"
              ? "linear-gradient(180deg, rgba(30,23,18,0.78) 0%, rgba(60,42,32,0.65) 55%, rgba(210,175,140,0.28) 100%)"
              : "linear-gradient(110deg, rgba(30,23,18,0.82) 0%, rgba(60,42,32,0.65) 45%, rgba(210,175,140,0.24) 100%)",
        }}
      />
      {/* Subtle warm halo — just enough to feel cinematic */}
      <div
        aria-hidden
        className="absolute -top-16 -right-10 w-[320px] h-[320px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,214,170,0.24), transparent 70%)",
          filter: "blur(10px)",
        }}
      />

      {/* N8ONE emblem — positioned top-right so the hero copy on the
          left remains the focal point. Non-interactive. Sits above the
          cinematic wash but below the eyebrow/title z-10 column. */}
      {emblem && emblem !== "none" && (
        <div
          className="absolute top-4 right-5 md:top-6 md:right-7 z-10 pointer-events-none"
          data-testid={`${testid}-emblem-wrap`}
        >
          <N8Emblem
            mode={emblem === "simplified" ? "simplified" : "full"}
            size={emblem === "simplified" ? 44 : 64}
            breathing={emblem === "guide" || emblem === "guide-active"}
            active={emblem === "guide-active"}
            ariaLabel={
              emblem === "guide" || emblem === "guide-active"
                ? "N8 Guide emblem"
                : "N8ONE emblem"
            }
            testid={`${testid}-emblem`}
          />
        </div>
      )}

      <div
        className={`relative z-10 px-6 py-6 md:px-10 md:py-7 max-w-3xl ${
          align === "center" ? "mx-auto text-center" : ""
        }`}
        style={{ animation: "reveal 700ms ease-out both" }}
      >
        {eyebrow && (
          <div
            className="text-[10.5px] uppercase tracking-[0.28em]"
            style={{ color: "rgba(246,241,232,0.78)" }}
            data-testid={`${testid}-eyebrow`}
          >
            {eyebrow}
          </div>
        )}
        <h1
          className="font-display text-2xl sm:text-3xl md:text-[34px] tracking-tight leading-[1.08] mt-2"
          style={{ color: "#F6F1E8" }}
          data-testid={`${testid}-title`}
        >
          {title}
        </h1>
        {supporting && (
          <p
            className="font-display italic text-[15px] md:text-base mt-2 max-w-2xl leading-snug"
            style={{ color: "#E19271" }}
            data-testid={`${testid}-supporting`}
          >
            {supporting}
          </p>
        )}
        {body && (
          <p
            className="mt-2 max-w-2xl text-[13px] leading-relaxed"
            style={{ color: "rgba(246,241,232,0.85)" }}
            data-testid={`${testid}-body`}
          >
            {body}
          </p>
        )}
        {children && <div className="mt-4">{children}</div>}
      </div>
    </section>
  );
}
